import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { User, Mail, Shield, LogOut, Crown, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { sendGA4, GA4 } from "../hooks/useAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, logout, checkAuth, login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [syncing, setSyncing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/stripe/sync-subscription`, {}, { withCredentials: true });
        if (res.data && res.data.user_id) {
          // Use sync response directly — it has the freshest plan from Stripe
          login(res.data);
        }
      } catch {
        // Sync failed — fall back to refreshing from DB via checkAuth
        try { await checkAuth(); } catch { /* auth check failed */ }
      }
      if (!cancelled) setSyncing(false);
    })();
    return () => { cancelled = true; };
  }, [checkAuth, login]);

  const handleLogout = async () => {
    await logout();
    toast.success(t("billing.logoutSuccess"));
    navigate("/");
  };

  const handleManageSubscription = async () => {
    sendGA4(GA4.MANAGE_SUBSCRIPTION_CLICKED);
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/create-portal-session`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch {
      toast.error(t("billing.portalError"));
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async () => {
    sendGA4(GA4.CLICK_START_PREMIUM, { source: "profile_page" });
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/create-checkout-session`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      sendGA4(GA4.CHECKOUT_STARTED, { source: "profile_page" });
      window.location.href = res.data.url;
    } catch {
      toast.error(t("billing.checkoutError"));
      setCheckoutLoading(false);
    }
  };

  if (!user) return null;

  const showPlan = syncing ? null : user.plan;
  const hasStripeCustomer = !!user.stripe_subscription_status && user.stripe_subscription_status === "active";

  return (
    <main className="min-h-screen bg-background" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="text-3xl font-bold text-slate-900 font-heading mb-8">{t("billing.profileTitle")}</h1>

        <Card className="border-slate-100 mb-6" data-testid="profile-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-slate-100" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-2xl font-bold text-teal-700">{user.name?.[0] || "U"}</span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{user.name}</h2>
                <p className="text-sm text-slate-500">{user.email}</p>
              </div>
            </div>

            <Separator className="mb-6" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{t("billing.name")}</span>
                </div>
                <span className="text-sm font-medium text-slate-900">{user.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{t("billing.email")}</span>
                </div>
                <span className="text-sm font-medium text-slate-900">{user.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{t("billing.authMethod")}</span>
                </div>
                <Badge variant="outline" className="capitalize text-xs">{user.auth_type || "email"}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{t("billing.plan")}</span>
                </div>
                <Badge className={`text-xs ${showPlan === "pro" ? "bg-teal-100 text-teal-700" : syncing ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-600"}`}>
                  {syncing ? "Syncing..." : showPlan === "pro" ? t("billing.proPlan") : t("billing.freePlan")}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {!syncing && showPlan === "pro" && hasStripeCustomer ? (
          <Card className="border-teal-200 bg-teal-50/50 mb-6" data-testid="manage-subscription-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <CreditCard className="h-5 w-5 text-teal-700" />
                <h3 className="text-base font-semibold text-slate-900">{t("billing.subscriptionActive")}</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                {t("billing.manageDesc")}
              </p>
              <Button
                className="bg-teal-700 hover:bg-teal-800 text-white font-medium"
                data-testid="manage-subscription-btn"
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("billing.redirecting")}</>
                ) : (
                  <><CreditCard className="mr-2 h-4 w-4" /> {t("billing.manageSubscription")}</>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-teal-200 bg-teal-50/50 mb-6" data-testid="upgrade-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Crown className="h-5 w-5 text-teal-700" />
                <h3 className="text-base font-semibold text-slate-900">{t("billing.upgradeToPremium")}</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                {t("billing.upgradeDesc")}
              </p>
              <Button
                className="bg-teal-700 hover:bg-teal-800 text-white font-medium"
                data-testid="upgrade-btn"
                onClick={handleUpgrade}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("billing.redirecting")}</>
                ) : (
                  t("billing.upgradeNow")
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        <Button variant="outline" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="profile-logout-btn">
          <LogOut className="mr-2 h-4 w-4" /> {t("billing.logout")}
        </Button>
      </div>
    </main>
  );
}
